import { ref, type Ref } from 'vue';

const MAX_HISTORY_SIZE = 25;
const DEFAULT_TRUNCATION_THRESHOLD_KB = 1000;

export interface ApiRequestRecord {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number | null;
  requestBody: unknown;
  responseBody: unknown;
  error?: string;
}

interface PendingRequest {
  id: string;
  startTime: number;
  method: string;
  url: string;
  requestBody: unknown;
}

// Module-level state (singleton pattern for interceptor access)
const requestHistory: Ref<ApiRequestRecord[]> = ref([]);
const pendingRequests = new Map<string, PendingRequest>();
const truncationThresholdKb: Ref<number> = ref(DEFAULT_TRUNCATION_THRESHOLD_KB);
let requestIdCounter = 0;

/**
 * Generates a unique request ID for tracking
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${++requestIdCounter}`;
}

/**
 * Estimates the size of a value in KB when serialized to JSON.
 * Falls back to String() representation when JSON serialization fails (e.g. circular refs).
 */
function estimateSizeKb(value: unknown): number {
  try {
    const json = JSON.stringify(value, (_k, v) => (typeof v === 'bigint' ? v.toString() : v));
    return json.length / 1024;
  } catch {
    try {
      return String(value).length / 1024;
    } catch {
      return 1; // Non-zero so truncation logic still applies
    }
  }
}

/**
 * Keys whose values should always be redacted from the debug export (case-insensitive).
 */
const SENSITIVE_KEYS = new Set([
  'passphrase',
  'token',
  'refreshtoken',
  'accesstoken',
  'idtoken',
  'auth',
  'authorization',
  'secret',
  'secretkey',
  'secret_key',
  'apikey',
  'api_key',
  'api-key',
  'accesskey',
  'access_key',
  'privatekey',
  'private_key',
  'private-key',
  'clientsecret',
  'client_secret',
  'client-secret',
  'credentials',
  'oauth',
  'bearer',
  'sessionid',
  'session_id',
  'csrf',
  'csrftoken',
]);

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key.toLowerCase());
}

/**
 * Detects if a string looks like base64 encoded data
 */
function isBase64Data(str: string): boolean {
  // Data URI pattern
  if (str.startsWith('data:')) {
    return true;
  }
  // Long base64-like string (at least 100 chars, mostly alphanumeric with +/=)
  if (str.length > 100 && /^[A-Za-z0-9+/=]{100,}$/.test(str)) {
    return true;
  }
  return false;
}

/**
 * Detects if a string is a blob URL
 */
function isBlobUrl(str: string): boolean {
  return str.startsWith('blob:');
}

/**
 * Recursively sanitizes data by replacing base64/blob content with placeholders.
 * This is exported for reuse by store export.
 */
export function sanitizeData(value: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > 20) {
    return '[MAX_DEPTH_REACHED]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  // Normalize BigInt to string so it can be safely serialized
  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'string') {
    if (isBlobUrl(value)) {
      return '[BLOB_URL]';
    }
    if (isBase64Data(value)) {
      const sizeKb = Math.round(value.length / 1024);
      return `[BASE64_DATA: ~${sizeKb}KB]`;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeData(item, depth + 1));
  }

  if (typeof value === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      // Skip functions and symbols
      if (typeof val === 'function' || typeof val === 'symbol') {
        continue;
      }
      // Redact known-sensitive fields regardless of their value
      if (isSensitiveKey(key)) {
        sanitized[key] = '[REDACTED]';
        continue;
      }
      sanitized[key] = sanitizeData(val, depth + 1);
    }
    return sanitized;
  }

  return value;
}

/**
 * Applies truncation to data if it exceeds the threshold
 */
function applyTruncation(value: unknown, thresholdKb: number): unknown {
  const sizeKb = estimateSizeKb(value);
  if (sizeKb <= thresholdKb) {
    return value;
  }

  // For strings, truncate directly
  if (typeof value === 'string') {
    const targetLength = Math.floor(thresholdKb * 1024);
    return `${value.substring(0, targetLength)}... [TRUNCATED: ${Math.round(sizeKb)}KB → ${thresholdKb}KB]`;
  }

  // For objects/arrays, serialize, truncate, and mark
  try {
    const json = JSON.stringify(value);
    const targetLength = Math.floor(thresholdKb * 1024);
    const truncated = json.substring(0, targetLength);
    return `${truncated}... [TRUNCATED: ${Math.round(sizeKb)}KB → ${thresholdKb}KB]`;
  } catch {
    return `[TRUNCATION_ERROR: ~${Math.round(sizeKb)}KB]`;
  }
}

/**
 * Starts tracking a request (called from request interceptor)
 */
export function startRequest(method: string, url: string, requestBody: unknown): string {
  const id = generateRequestId();
  const sanitizedBody = sanitizeData(requestBody);

  pendingRequests.set(id, {
    id,
    startTime: performance.now(),
    method: method.toUpperCase(),
    url,
    requestBody: sanitizedBody,
  });

  return id;
}

/**
 * Completes a request record (called from response interceptor)
 */
export function completeRequest(
  id: string,
  status: number | null,
  responseBody: unknown,
  error?: string,
): void {
  const pending = pendingRequests.get(id);
  if (!pending) {
    return;
  }

  pendingRequests.delete(id);

  const durationMs = Math.round(performance.now() - pending.startTime);
  const sanitizedResponse = sanitizeData(responseBody);
  const truncatedResponse = applyTruncation(sanitizedResponse, truncationThresholdKb.value);

  const record: ApiRequestRecord = {
    id: pending.id,
    timestamp: new Date().toISOString(),
    method: pending.method,
    url: pending.url,
    status,
    durationMs,
    requestBody: applyTruncation(pending.requestBody, truncationThresholdKb.value),
    responseBody: truncatedResponse,
    error,
  };

  // Add to history, maintaining max size (circular buffer)
  requestHistory.value.push(record);
  if (requestHistory.value.length > MAX_HISTORY_SIZE) {
    requestHistory.value.shift();
  }
}

/**
 * Composable for accessing debug request history
 */
export function useDebugRequestHistory() {
  /**
   * Gets the current request history
   */
  function getHistory(): ApiRequestRecord[] {
    return [...requestHistory.value];
  }

  /**
   * Clears all request history
   */
  function clearHistory(): void {
    requestHistory.value = [];
    pendingRequests.clear();
  }

  /**
   * Gets the current request count
   */
  function getRequestCount(): number {
    return requestHistory.value.length;
  }

  /**
   * Gets the max history size
   */
  function getMaxSize(): number {
    return MAX_HISTORY_SIZE;
  }

  /**
   * Sets the truncation threshold in KB
   */
  function setTruncationThreshold(kb: number): void {
    truncationThresholdKb.value = Math.max(1, kb);
  }

  /**
   * Gets the current truncation threshold in KB
   */
  function getTruncationThreshold(): number {
    return truncationThresholdKb.value;
  }

  return {
    history: requestHistory,
    truncationThreshold: truncationThresholdKb,
    getHistory,
    clearHistory,
    getRequestCount,
    getMaxSize,
    setTruncationThreshold,
    getTruncationThreshold,
  };
}
